import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
} from '@angular/core';
import { firstValueFrom, ReplaySubject } from 'rxjs';
import { delay } from 'rxjs/operators';
import { OnLoaded } from '@lyceo/shared/dynamic-component-loading/feature-main';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { NgTemplateOutlet } from '@angular/common';
import { QuestionTableData } from '../question-table-service/question-table.service';
import { DisplayMathPipe } from '../display-math/display-math.pipe';

@Component({
  selector: 'shared-question-table',
  templateUrl: './question-table.component.html',
  styleUrls: ['./question-table.component.scss'],
  imports: [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    NgTemplateOutlet,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    DisplayMathPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionTableComponent implements AfterViewInit, OnLoaded {
  /**
   * Local QuestionTableData.
   */
  public data!: QuestionTableData;

  /**
   * Columns to display in Material Table.
   */
  public displayedColumns: string[] = [];

  /**
   * DataSource for Material Table;
   */
  public dataSource!: { [index: string]: string }[];

  /**
   * Use sticky header row flag.
   */
  public hasHeaderRow!: boolean;

  /**
   * Subject which starts to emit when the view is rendered. This is needed because the
   * component is dynamically loading.
   */
  public onFinishedInit$: ReplaySubject<void> = new ReplaySubject<void>();

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    this.onFinishedInit$.next();
    this.onFinishedInit$.complete();
  }

  /**
   * @inheritDoc
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.onFinishedInit$.pipe(delay(20)));
  }

  /**
   * Set local data from the provided QuestionTableData.
   */
  public setData(data: QuestionTableData): void {
    this.data = data;

    this.displayedColumns = data.columns.map((col) => col.key);
    this.dataSource = this.data.rows.map((row) => row.cells);

    // Material Table only uses a Header Row with column names
    this.hasHeaderRow = data.rows[0].isSticky;

    // ...so if there is a header row, remove the first 'row' from dataSource
    if (this.hasHeaderRow) {
      this.dataSource = this.dataSource.slice(1);
    }
  }
}
